import { createAttractionThemesConfig, type AttractionTheme } from '@/config/objects/attraction-themes';
import { CrudListView } from '@startupquickstart/frontend';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface AttractionThemesListProps {
  attractionId: string;
  className?: string;
}

/**
 * Attraction themes list component
 * Displays and manages themes for a specific attraction
 */
export function AttractionThemesList({ attractionId, className }: AttractionThemesListProps) {
  const navigate = useNavigate();

  const handleCreateTheme = useCallback(() => {
    navigate(`/attractions/${attractionId}/themes/new`);
  }, [attractionId, navigate]);

  const themesConfig = useMemo(
    () =>
      createAttractionThemesConfig(attractionId, {
        onCreateTheme: handleCreateTheme,
      }),
    [attractionId, handleCreateTheme],
  );

  const handleItemClick = useCallback(
    (theme: AttractionTheme) => {
      navigate(`/attractions/${attractionId}/themes/${theme.id}`);
    },
    [attractionId, navigate],
  );

  return (
    <div className={className}>
      <CrudListView
        config={themesConfig}
        baseUrl={`/attractions/${attractionId}/themes`}
        onItemClick={handleItemClick}
        getValueFromItem={(theme: AttractionTheme) => theme.id}
        getLabelFromItem={(theme: AttractionTheme) =>
          theme.name || theme.themeType?.name || 'Unnamed Theme'
        }
      />
    </div>
  );
}
